// src/components/CustomerDocsModal.tsx
//
// Per-customer AR document actions from the AR Detail view: view / print / email
// a statement, selected invoices, or both combined. Statement data comes from
// analytics-ar-drilldown; invoice line items from customer-invoice. PDFs are
// generated client-side (customerDocs). Email goes through send-customer-doc
// (demo tenant only for now). The email template (default or a tenant override
// in tenant_config) is editable inline and can be saved as the tenant default.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, Printer, Mail, Loader2, FileText, Check } from 'lucide-react';
import { apiV2 as api } from '../services/apiV2';
import { adminApi } from '../services/adminApi';
import { useAuthContext } from '../hooks/useAuth';
import Modal from './Modal';
import { InvoicePdfButtons } from './ARInvoiceLines';
import {
  buildStatementPdf, buildInvoicePdf, openPdf, printPdf, pdfBase64, skippedOrUnderpaid,
  type StatementData, type InvoiceData,
} from '../lib/customerDocs';
import { DEFAULT_TEMPLATE, renderTemplate, bodyToHtml } from '../lib/customerDocTemplate';

const money = (n: number) => (n < 0 ? `(${Math.abs(n).toFixed(2)})` : n.toFixed(2));

export default function CustomerDocsModal({ code, name, onClose }: { code: number; name: string; onClose: () => void }) {
  const { branding } = useAuthContext();
  const company = branding?.company_name || 'Statement';

  const { data: dd, isLoading } = useQuery({
    queryKey: ['custdoc-dd', code],
    queryFn: () => api.getARDrilldown(code),
    retry: 1,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [tplLoaded, setTplLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Load template (tenant override or built-in default) once.
  useQuery({
    queryKey: ['custdoc-tpl'],
    enabled: !tplLoaded,
    staleTime: Infinity, retry: 0,
    queryFn: async () => {
      const [s, b] = await Promise.all([
        adminApi.getConfig('customer_docs.email_subject').catch(() => null),
        adminApi.getConfig('customer_docs.email_body').catch(() => null),
      ]);
      setSubject(s?.value || DEFAULT_TEMPLATE.subject);
      setBody(b?.value || DEFAULT_TEMPLATE.body);
      setTplLoaded(true);
      return true;
    },
  });

  const statementData: StatementData | null = useMemo(() => {
    if (!dd) return null;
    return {
      customer: {
        code: dd.customer.code, name: dd.customer.name, email: dd.customer.email,
        phone: dd.customer.phone, terms: dd.customer.terms, balance: dd.customer.balance,
      },
      invoices: dd.invoices,
    };
  }, [dd]);

  const email = dd?.customer.email?.trim() || '';
  const totalDue = dd ? dd.invoices.reduce((s, i) => s + i.balance, 0) : 0;
  const hasSelection = selected.size > 0;

  function renderedTemplate() {
    const flagged = dd ? skippedOrUnderpaid(dd.invoices) : [];
    const lines = flagged.map((i) => `Invoice ${i.invoice} (${i.date.slice(0, 10)}) — balance ${money(i.balance)}`);
    return renderTemplate({ subject, body }, { customerName: name, companyName: company, balance: money(totalDue), skippedLines: lines });
  }

  async function invoiceAttachments(nums: string[]) {
    const out: { filename: string; contentBase64: string }[] = [];
    for (const n of nums) {
      const inv = await api.getCustomerInvoice(n);
      out.push({ filename: `Invoice-${n}.pdf`, contentBase64: pdfBase64(buildInvoicePdf(inv as InvoiceData, company)) });
    }
    return out;
  }

  function viewStatement(print = false) {
    if (!statementData) return;
    const doc = buildStatementPdf(statementData, company);
    print ? printPdf(doc) : openPdf(doc);
  }

  async function doEmail(kind: 'statement' | 'invoices' | 'combined') {
    if (!statementData) return;
    if (!email) { setMsg({ ok: false, text: 'No email address on file for this customer.' }); return; }
    setBusy(kind); setMsg(null);
    try {
      const attachments: { filename: string; contentBase64: string }[] = [];
      if (kind === 'statement' || kind === 'combined') {
        attachments.push({ filename: `Statement-${code}.pdf`, contentBase64: pdfBase64(buildStatementPdf(statementData, company)) });
      }
      if (kind === 'invoices' || kind === 'combined') {
        attachments.push(...await invoiceAttachments([...selected]));
      }
      const t = renderedTemplate();
      const res = await api.sendCustomerDoc({ to: [email], subject: t.subject, html: bodyToHtml(t.body), attachments });
      setMsg({ ok: true, text: `Sent to ${email} (${res.recipients} recipient).` });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  }

  async function saveTemplate() {
    setBusy('save-tpl'); setMsg(null);
    try {
      await adminApi.putConfig('customer_docs.email_subject', subject);
      await adminApi.putConfig('customer_docs.email_body', body);
      setMsg({ ok: true, text: 'Template saved as the tenant default.' });
    } catch (e) { setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(null); }
  }

  const toggle = (n: string) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(n) ? next.delete(n) : next.add(n);
    return next;
  });

  const Btn = ({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) => (
    <button onClick={onClick} disabled={disabled || !!busy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700/60 border border-slate-600/50 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50 transition-colors">
      {children}
    </button>
  );

  return (
    <Modal onClose={onClose} panelClassName="max-w-3xl" showHeader={false}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-blue-400" />
          <h3 className="text-lg font-semibold text-white">Customer Documents — {name} (#{code})</h3>
        </div>
      </div>

      {isLoading ? (
        <div className="py-10 text-center text-slate-400"><Loader2 className="animate-spin inline" size={18} /> Loading…</div>
      ) : !dd ? (
        <div className="py-10 text-center text-slate-400">No AR data for this customer.</div>
      ) : (
        <div className="space-y-4">
          {/* Statement actions */}
          <div className="p-3 rounded-xl border border-slate-700/50 bg-slate-800/30">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-white">Statement</div>
              <div className="text-sm text-slate-400">Total due <span className="text-white font-semibold">{money(totalDue)}</span> · {dd.invoices.length} open</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Btn onClick={() => viewStatement(false)}><Eye size={14} /> View</Btn>
              <Btn onClick={() => viewStatement(true)}><Printer size={14} /> Print</Btn>
              <Btn onClick={() => doEmail('statement')} disabled={!email}>{busy === 'statement' ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email</Btn>
              {!email && <span className="text-xs text-amber-400 self-center">no email on file</span>}
            </div>
          </div>

          {/* Open invoices */}
          <div className="p-3 rounded-xl border border-slate-700/50 bg-slate-800/30">
            <div className="text-sm font-medium text-white mb-2">Invoices ({selected.size} selected)</div>
            <div className="max-h-52 overflow-auto rounded-lg border border-slate-700/40">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60 text-slate-400 text-xs sticky top-0">
                  <tr><th className="w-8 p-2"></th><th className="text-left p-2">Invoice</th><th className="text-left p-2">Date</th><th className="text-right p-2">Balance</th><th className="text-left p-2">Status</th><th className="p-2"></th></tr>
                </thead>
                <tbody>
                  {dd.invoices.map((i) => (
                    <tr key={i.invoice} className="border-t border-slate-700/30">
                      <td className="p-2 text-center">
                        <input type="checkbox" checked={selected.has(i.invoice)} onChange={() => toggle(i.invoice)} className="accent-blue-500" />
                      </td>
                      <td className="p-2 text-slate-200">{i.invoice}</td>
                      <td className="p-2 text-slate-400">{i.date.slice(0, 10)}</td>
                      <td className="p-2 text-right text-slate-200">{money(i.balance)}</td>
                      <td className="p-2 text-xs">
                        {i.flags.includes('skipped') && <span className="text-red-300">Skipped</span>}
                        {i.flags.includes('shortpaid') && <span className="text-amber-300">Underpaid</span>}
                      </td>
                      <td className="p-2 text-right">
                        <InvoicePdfButtons invoice={i.invoice} onError={(text) => setMsg({ ok: false, text })} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <Btn onClick={() => doEmail('invoices')} disabled={!email || !hasSelection}>{busy === 'invoices' ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email selected</Btn>
              <Btn onClick={() => doEmail('combined')} disabled={!email || !hasSelection}>{busy === 'combined' ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email statement + selected</Btn>
            </div>
          </div>

          {/* Email template */}
          <details className="p-3 rounded-xl border border-slate-700/50 bg-slate-800/30">
            <summary className="text-sm font-medium text-white cursor-pointer">Email template</summary>
            <div className="mt-3 space-y-2">
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
                className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/50 text-sm text-white" />
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7}
                className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/50 text-sm text-white font-mono" />
              <div className="text-[11px] text-slate-500">Placeholders: {'{customerName}'} {'{companyName}'} {'{balance}'} {'{skippedBlock}'} — the skipped/underpaid list fills in automatically.</div>
              <div className="flex gap-2">
                <Btn onClick={() => { setSubject(DEFAULT_TEMPLATE.subject); setBody(DEFAULT_TEMPLATE.body); }}>Reset to default</Btn>
                <Btn onClick={saveTemplate}>{busy === 'save-tpl' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save as tenant default</Btn>
              </div>
            </div>
          </details>

          {msg && (
            <div className={`text-sm px-3 py-2 rounded-lg ${msg.ok ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20' : 'bg-red-500/10 text-red-300 border border-red-500/20'}`}>
              {msg.text}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
